import React, { useState } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
// Using text instead of icons for better compatibility
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import './CreditNotesManager.css';
import { formatMoney } from '../../utils/formatMoney';

const CreditNotesManager = ({ order, onUpdate, admin }) => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);

    const calculateCreditNoteTotal = (creditNote) => {
        if (!creditNote || !creditNote.items) return 0;
        
        let total = 0;
        creditNote.items.forEach(item => {
            total += item.amount * item.price;
        });
        
        total += (creditNote.surcharge || 0);
        total += (creditNote.shipping || 0);
        
        return total;
    };

    const getTotalCreditAmount = () => {
        if (!order.creditNotes || order.creditNotes.length === 0) return 0;
        
        return order.creditNotes.reduce((total, creditNote) => {
            return total + calculateCreditNoteTotal(creditNote);
        }, 0);
    };

    const handleDeleteCreditNote = async (creditNoteId) => {
        if (!window.confirm('Are you sure you want to delete this credit note?')) {
            return;
        }

        setLoading(true);
        try {
            const response = await fetch(`${process.env.REACT_APP_API}/api/orders/${order._id}/creditnotes/${creditNoteId}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                toast.success('Credit note deleted successfully!');
                onUpdate(); // Refresh the order data
            } else {
                toast.error('Failed to delete credit note.');
            }
        } catch (error) {
            console.error('Error deleting credit note:', error);
            toast.error('Error deleting credit note.');
        } finally {
            setLoading(false);
        }
    };

    const handleEditCreditNote = (creditNoteId) => {
        navigate(`/creditnote/${order._id}?creditNoteId=${creditNoteId}`);
    };

    if (!order.creditNotes || order.creditNotes.length === 0) {
        return (
            <div className="credit-notes-manager">
                <div className="credit-notes-header">
                    <h4>Credit Notes</h4>
                </div>
                <p className="credit-notes-empty-hint">No credit notes found for this order.</p>
            </div>
        );
    }

    return (
        <div className="credit-notes-manager">
            <div className="credit-notes-header">
                <h4>Credit Notes ({order.creditNotes.length})</h4>
            </div>
            
            <TableContainer component={Paper} className="credit-notes-table">
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Credit Note #</TableCell>
                            <TableCell>Date</TableCell>
                            <TableCell align="right">Items</TableCell>
                            <TableCell align="right">Amount</TableCell>
                            {admin === "true" && <TableCell align="center">Actions</TableCell>}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {order.creditNotes.map((creditNote, index) => (
                            <TableRow key={creditNote._id || index}>
                                <TableCell>
                                    <strong>{creditNote.creditNoteNumber}</strong>
                                </TableCell>
                                <TableCell>
                                    {new Intl.DateTimeFormat("en-GB", { 
                                        year: "numeric", 
                                        month: "2-digit", 
                                        day: "2-digit" 
                                    }).format(new Date(creditNote.date))}
                                </TableCell>
                                <TableCell align="right">
                                    {creditNote.items ? creditNote.items.length : 0}
                                </TableCell>
                                <TableCell align="right">
                                    <strong>-€{formatMoney(calculateCreditNoteTotal(creditNote))}</strong>
                                </TableCell>
                                {admin === "true" && (
                                    <TableCell align="center">
                                        <Button
                                            size="small"
                                            onClick={() => handleEditCreditNote(creditNote._id)}
                                            title="View/Edit Credit Note"
                                            style={{ marginRight: '5px', minWidth: '60px' }}
                                        >
                                            Edit
                                        </Button>
                                        <Button
                                            size="small"
                                            onClick={() => handleDeleteCreditNote(creditNote._id)}
                                            title="Delete Credit Note"
                                            disabled={loading}
                                            color="error"
                                            style={{ minWidth: '60px' }}
                                        >
                                            Delete
                                        </Button>
                                    </TableCell>
                                )}
                            </TableRow>
                        ))}
                        <TableRow>
                            <TableCell colSpan={3}>
                                <strong>Total Credit Amount</strong>
                            </TableCell>
                            <TableCell align="right">
                                <strong>-€{formatMoney(getTotalCreditAmount())}</strong>
                            </TableCell>
                            {admin === "true" && <TableCell />}
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>
        </div>
    );
};

export default CreditNotesManager;
